<template>
  <section class="page-card">
    <div class="page-head">
      <h1 class="page-title">{{ $t('pages.earningsWithdrawOrders') }}</h1>
      <div class="wallet-balance">
        <span>{{ $t('exagentWithdraw.availableTopupBalance') }}</span>
        <strong>{{ wallet.topup_balance || '0' }} {{ wallet.currency_code || '' }}</strong>
      </div>
      <el-button
        type="primary"
        :loading="openingApply"
        :disabled="hasUnfinishedOrder"
        @click="openApplyDialog"
      >{{ $t('exagentWithdraw.apply') }}</el-button>
    </div>

    <el-alert
      v-if="hasUnfinishedOrder"
      :title="$t('exagentWithdraw.unfinishedWarning')"
      type="warning"
      show-icon
      :closable="false"
      class="page-alert"
    ></el-alert>

    <div class="filter-bar">
      <el-input v-model.trim="search.order_no" :placeholder="$t('common.orderId')" class="filter-item" size="small"></el-input>
      <el-select v-model="search.status" clearable :placeholder="$t('common.status')" class="filter-item" size="small">
        <el-option v-for="item in withdrawStatuses" :key="item.value" :value="item.value" :label="$t(item.labelKey)"></el-option>
      </el-select>
      <el-button type="primary" size="small" @click="reload">{{ $t('actions.search') }}</el-button>
    </div>

    <div class="table-scroll">
      <el-table :key="tableLanguage" :data="tableData" border stripe>
        <el-table-column prop="order_no" :label="$t('common.orderId')" min-width="180"></el-table-column>
        <el-table-column prop="amount" :label="$t('exagentWithdraw.applicationAmount')" min-width="130"></el-table-column>
        <el-table-column prop="fee_amount" :label="$t('exagentWithdraw.feeAmount')" min-width="120"></el-table-column>
        <el-table-column prop="payout_amount" :label="$t('exagentWithdraw.expectedPayout')" min-width="130"></el-table-column>
        <el-table-column prop="currency_code" :label="$t('common.currency')" min-width="90"></el-table-column>
        <el-table-column :label="$t('common.payMethod')" min-width="130">
          <template slot-scope="scope">{{ payTypeText(scope.row.pay_type) }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.reviewStatus')" min-width="140">
          <template slot-scope="scope">{{ reviewStatusText(scope.row.check_status) }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.payStatus')" min-width="140">
          <template slot-scope="scope">{{ statusText(scope.row.status) }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.failReason')" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.fail_reason || '-' }}</template>
        </el-table-column>
        <el-table-column prop="third_no" :label="$t('common.thirdOrderNo')" min-width="170"></el-table-column>
        <el-table-column prop="created_at" :label="$t('common.applicationTime')" min-width="160"></el-table-column>
        <el-table-column prop="completed_at" :label="$t('common.completedAt')" min-width="160"></el-table-column>
        <el-table-column :label="$t('common.actions')" width="100" class-name="table-actions">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openDetail(scope.row)">{{ $t('actions.detail') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <el-pagination
      :total="count"
      :current-page.sync="search.page"
      :page-size="search.size"
      layout="total, prev, pager, next"
      @current-change="loadData"
    ></el-pagination>

    <el-dialog
      :title="$t('pages.earningsWithdraw')"
      :visible.sync="applyVisible"
      width="760px"
      :show-close="!previewing"
      :close-on-click-modal="!previewing"
      :close-on-press-escape="!previewing"
      @closed="resetApplyDialog"
    >
      <div class="apply-balance">
        <span>{{ $t('exagentWithdraw.availableTopupBalance') }}</span>
        <strong>{{ wallet.topup_balance || '0' }} {{ selectedCurrency }}</strong>
      </div>
      <el-alert
        v-if="optionsLoadedSuccessfully && !options.length"
        :title="$t('exagentWithdraw.noBank')"
        type="info"
        show-icon
        :closable="false"
        class="dialog-alert"
      ></el-alert>
      <el-form label-position="top">
        <el-form-item :label="$t('exagentWithdraw.bank')" required>
          <el-select v-model="form.bank_code" class="form-control" filterable @change="changeBank">
            <el-option
              v-for="item in options"
              :key="item.bank_code"
              :label="item.name || item.bank_code"
              :value="item.bank_code"
            ></el-option>
          </el-select>
          <div v-if="selectedConf" class="form-tip">
            {{ formatMessage('exagentWithdraw.channelLimits', {
              min: selectedConf.min_amount,
              max: selectedConf.max_amount,
              align: selectedConf.amount_align_unit || 1
            }) }}
          </div>
        </el-form-item>

        <el-form-item :label="$t('exagentWithdraw.amount')" required>
          <el-input v-model.trim="form.amount" class="form-control" autocomplete="off">
            <template slot="append">{{ selectedCurrency }}</template>
          </el-input>
        </el-form-item>

        <template v-if="selectedConf">
          <h2 class="section-title">{{ $t('exagentWithdraw.payeeInformation') }}</h2>
          <div class="field-grid">
            <el-form-item
              v-for="field in selectedConf.fields"
              :key="field.key"
              :label="fieldLabel(field.key)"
              :required="field.required"
            >
              <el-select
                v-if="field.key === 'bankName' && selectedConf.banks && selectedConf.banks.length"
                v-model="form.payee_info[field.key]"
                class="form-control"
                filterable
                :disabled="loadingPayeeInfo"
              >
                <el-option
                  v-for="bank in selectedConf.banks"
                  :key="bank.code"
                  :label="bank.name || bank.code"
                  :value="bank.code"
                ></el-option>
              </el-select>
              <el-select
                v-else-if="field.key === 'transferNetwork'"
                v-model="form.payee_info[field.key]"
                class="form-control"
                :disabled="loadingPayeeInfo"
              >
                <el-option label="TRC20" value="USDT_TRC20"></el-option>
                <el-option label="ERC20" value="USDT_ERC20"></el-option>
                <el-option label="BSC" value="USDT_BSC"></el-option>
              </el-select>
              <el-input
                v-else-if="field.key === 'accountNumber'"
                v-model.trim="form.payee_info[field.key]"
                :disabled="loadingPayeeInfo"
                type="tel"
                autocomplete="off"
              >
                <template slot="prepend">+{{ areaCode }}</template>
              </el-input>
              <el-input
                v-else
                v-model.trim="form.payee_info[field.key]"
                :disabled="loadingPayeeInfo"
                autocomplete="off"
              ></el-input>
            </el-form-item>
          </div>
        </template>
      </el-form>

      <span slot="footer">
        <el-button :disabled="previewing" @click="applyVisible = false">{{ $t('actions.cancel') }}</el-button>
        <el-button
          type="primary"
          :loading="previewing"
          :disabled="submitDisabled"
          @click="submitWithdraw"
        >{{ previewing ? $t('exagentWithdraw.previewing') : $t('actions.submit') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('exagentWithdraw.confirmTitle')"
      :visible.sync="confirmVisible"
      width="620px"
      :show-close="!submitting"
      :close-on-click-modal="!submitting"
      :close-on-press-escape="!submitting"
      @closed="clearConfirmation"
    >
      <div v-if="previewQuote" class="detail-grid confirmation-grid">
        <div>
          <span>{{ $t('exagentWithdraw.applicationAmount') }}</span>
          <strong>{{ previewQuote.amount }} {{ previewQuote.currency_code }}</strong>
        </div>
        <div>
          <span>{{ $t('exagentWithdraw.feeRate') }}</span>
          <strong>{{ previewQuote.fee_rate }}%</strong>
        </div>
        <div>
          <span>{{ $t('exagentWithdraw.feeAmount') }}</span>
          <strong>{{ previewQuote.fee_amount }} {{ previewQuote.currency_code }}</strong>
        </div>
        <div>
          <span>{{ $t('exagentWithdraw.expectedPayout') }}</span>
          <strong>{{ previewQuote.payout_amount }} {{ previewQuote.currency_code }}</strong>
        </div>
        <div>
          <span>{{ $t('exagentWithdraw.payMethod') }}</span>
          <strong>{{ confirmationPayMethod }}</strong>
        </div>
        <div>
          <span>{{ $t('exagentWithdraw.payeeInformation') }}</span>
          <strong>{{ confirmationPayee }}</strong>
        </div>
      </div>
      <span slot="footer">
        <el-button :disabled="submitting" @click="clearConfirmation">{{ $t('actions.cancel') }}</el-button>
        <el-button
          type="primary"
          :loading="submitting"
          :disabled="!previewQuote || !pendingPayload"
          @click="confirmWithdraw"
        >{{ $t('exagentWithdraw.confirmWithdraw') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="$t('exagentWithdraw.orderDetail')" :visible.sync="detailVisible" width="760px">
      <template v-if="detail">
        <div class="detail-grid">
          <div><span>{{ $t('common.orderId') }}</span><strong>{{ detail.order_no }}</strong></div>
          <div><span>{{ $t('exagentWithdraw.applicationAmount') }}</span><strong>{{ detail.amount }} {{ detail.currency_code }}</strong></div>
          <div><span>{{ $t('exagentWithdraw.feeRate') }}</span><strong>{{ detail.fee_rate }}%</strong></div>
          <div><span>{{ $t('exagentWithdraw.feeAmount') }}</span><strong>{{ detail.fee_amount }} {{ detail.currency_code }}</strong></div>
          <div><span>{{ $t('exagentWithdraw.expectedPayout') }}</span><strong>{{ detail.payout_amount }} {{ detail.currency_code }}</strong></div>
          <div><span>{{ $t('common.status') }}</span><strong>{{ statusText(detail.status) }}</strong></div>
          <div><span>{{ $t('common.thirdOrderNo') }}</span><strong>{{ detail.third_no || '-' }}</strong></div>
          <div><span>{{ $t('exagentWithdraw.auditRemark') }}</span><strong>{{ detail.audit_remark || '-' }}</strong></div>
          <div><span>{{ $t('common.failReason') }}</span><strong>{{ detail.fail_reason || '-' }}</strong></div>
        </div>

        <h2 class="section-title">{{ $t('exagentWithdraw.payeeInformation') }}</h2>
        <div class="detail-grid">
          <div v-for="(value, key) in detail.payee_info" :key="key">
            <span>{{ fieldLabel(key) }}</span><strong>{{ value || '-' }}</strong>
          </div>
        </div>

        <h2 class="section-title">{{ $t('common.status') }}</h2>
        <el-timeline>
          <el-timeline-item :timestamp="detail.created_at">{{ $t('exagentWithdraw.timelineApplied') }}</el-timeline-item>
          <el-timeline-item v-if="detail.audited_at" :timestamp="detail.audited_at">{{ $t('exagentWithdraw.timelineAudited') }}</el-timeline-item>
          <el-timeline-item v-if="detail.completed_at" :timestamp="detail.completed_at">{{ $t('exagentWithdraw.timelineCompleted') }}</el-timeline-item>
        </el-timeline>
      </template>
    </el-dialog>
  </section>
</template>

<script>
export default {
  name: 'EarningsWithdrawOrders',
  data() {
    return {
      search: { page: 1, size: 20, order_no: '', status: '' },
      tableData: [],
      count: 0,
      detail: null,
      detailVisible: false,
      wallet: {},
      options: [],
      areaCode: '',
      optionsLoadedSuccessfully: false,
      hasUnfinishedOrder: false,
      applyVisible: false,
      confirmVisible: false,
      openingApply: false,
      previewing: false,
      submitting: false,
      previewQuote: null,
      pendingPayload: null,
      confirmationPayMethod: '',
      confirmationPayee: '',
      loadingPayeeInfo: false,
      payeeLoadVersion: 0,
      previewLoadVersion: 0,
      form: {
        bank_code: '',
        amount: '',
        payee_info: {}
      },
      withdrawStatuses: [
        { value: 0, labelKey: 'exagentWithdrawStatus.pendingReview' },
        { value: 1, labelKey: 'exagentWithdrawStatus.rejected' },
        { value: 2, labelKey: 'exagentWithdrawStatus.payoutProcessing' },
        { value: 3, labelKey: 'exagentWithdrawStatus.success' },
        { value: 4, labelKey: 'exagentWithdrawStatus.failed' },
        { value: 5, labelKey: 'exagentWithdrawStatus.compensate' }
      ]
    };
  },
  computed: {
    tableLanguage() {
      return this.$i18nState.language;
    },
    selectedConf() {
      return this.options.find(item => String(item.bank_code) === String(this.form.bank_code)) || null;
    },
    selectedCurrency() {
      return (this.selectedConf && this.selectedConf.currency_code) || this.wallet.currency_code || '';
    },
    submitDisabled() {
      const amount = this.numberValue(this.form.amount);
      const balance = this.numberValue(this.wallet.topup_balance);
      return this.hasUnfinishedOrder ||
        this.previewing ||
        this.loadingPayeeInfo ||
        !this.selectedConf ||
        !this.options.length ||
        !Number.isFinite(amount) ||
        amount <= 0 ||
        amount > balance;
    }
  },
  created() {
    this.loadWallet();
    this.loadOptions();
    this.loadData();
  },
  methods: {
    formatMessage(key, values) {
      return Object.keys(values).reduce(
        (text, name) => String(text).replace(`{${name}}`, values[name]),
        this.$t(key)
      );
    },
    fieldLabel(key) {
      return this.$t(`withdrawFields.${key}`);
    },
    numberValue(value) {
      const parsed = Number(String(value || '').replace(/,/g, ''));
      return Number.isFinite(parsed) ? parsed : NaN;
    },
    payTypeText(value) {
      return this.$t(Number(value) === 0 ? 'withdrawPayType.bank' : 'withdrawPayType.usdt');
    },
    statusText(value) {
      this.tableLanguage;
      const item = this.withdrawStatuses.find(status => String(status.value) === String(value));
      return item ? this.$t(item.labelKey) : String(value);
    },
    reviewStatusText(value) {
      const labels = {
        0: 'reviewRecordStatus.ready',
        1: 'reviewRecordStatus.programPass',
        2: 'reviewRecordStatus.humanPass',
        3: 'reviewRecordStatus.programReject',
        4: 'reviewRecordStatus.humanReject'
      };
      return labels[value] ? this.$t(labels[value]) : String(value);
    },
    reload() {
      this.search.page = 1;
      this.loadData();
    },
    queryParams() {
      return Object.keys(this.search).reduce((params, key) => {
        const value = this.search[key];
        if (value !== '' && value !== null && value !== undefined) params[key] = value;
        return params;
      }, {});
    },
    resetApplyDialog() {
      this.payeeLoadVersion += 1;
      this.previewLoadVersion += 1;
      this.loadingPayeeInfo = false;
      this.previewing = false;
      this.clearConfirmation();
      this.form = { bank_code: '', amount: '', payee_info: {} };
    },
    async loadWallet() {
      try {
        const resp = await this.$axios.get('/admin/exagent/console/wallet/');
        if (resp.data.status !== 0) return false;
        this.wallet = resp.data.data || {};
        return true;
      } catch (error) {
        return false;
      }
    },
    async loadOptions() {
      this.optionsLoadedSuccessfully = false;
      try {
        const resp = await this.$axios.get('/admin/exagent/console/funds/withdraw/options/');
        if (resp.data.status !== 0) return false;
        const data = resp.data.data || {};
        this.options = data.list || [];
        this.areaCode = String(data.area_code || '').replace(/^\+/, '');
        this.hasUnfinishedOrder = Boolean(data.has_unfinished_order);
        this.optionsLoadedSuccessfully = true;
        return true;
      } catch (error) {
        return false;
      }
    },
    async openApplyDialog() {
      if (this.openingApply || this.hasUnfinishedOrder) return;
      this.openingApply = true;
      try {
        const results = await Promise.all([this.loadWallet(), this.loadOptions()]);
        const walletLoaded = results[0];
        const optionsLoaded = results[1];
        if (!walletLoaded || !optionsLoaded) return;
        if (this.hasUnfinishedOrder) return;
        this.resetApplyDialog();
        this.applyVisible = true;
        if (this.options.length) {
          this.form.bank_code = this.options[0].bank_code;
          await this.changeBank(this.form.bank_code);
        }
      } finally {
        this.openingApply = false;
      }
    },
    async changeBank(bankCode) {
      this.previewLoadVersion += 1;
      this.previewing = false;
      this.clearConfirmation();
      const loadVersion = ++this.payeeLoadVersion;
      const conf = this.options.find(item => String(item.bank_code) === String(bankCode));
      const nextInfo = {};
      (conf && conf.fields || []).forEach(field => {
        nextInfo[field.key] = '';
      });
      this.form.payee_info = nextInfo;
      if (!conf) return;
      this.loadingPayeeInfo = true;
      try {
        const resp = await this.$axios.get('/admin/exagent/console/funds/withdraw/last-info/', {
          params: { pay_type: conf.pay_type }
        });
        if (resp.data.status !== 0) return;
        if (loadVersion !== this.payeeLoadVersion) return;
        if (String(this.form.bank_code) !== String(bankCode)) return;
        const previous = (resp.data.data || {}).payee_info || {};
        Object.keys(nextInfo).forEach(key => {
          if (previous[key] !== undefined && previous[key] !== null) nextInfo[key] = String(previous[key]);
        });
        this.form.payee_info = Object.assign({}, nextInfo);
      } finally {
        if (loadVersion === this.payeeLoadVersion) this.loadingPayeeInfo = false;
      }
    },
    validate() {
      const amount = this.numberValue(this.form.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        this.$message.error(this.$t('exagentWithdraw.amountRequired'));
        return false;
      }
      if (amount > this.numberValue(this.wallet.topup_balance)) {
        this.$message.error(this.$t('exagentWithdraw.insufficientBalance'));
        return false;
      }
      const missing = (this.selectedConf.fields || []).find(field => field.required && !this.form.payee_info[field.key]);
      if (missing) {
        this.$message.error(this.formatMessage('exagentWithdraw.requiredField', { field: this.fieldLabel(missing.key) }));
        return false;
      }
      return true;
    },
    formatPayee(payeeInfo = this.form.payee_info, fields = []) {
      return fields.map(field => {
        let value = String(payeeInfo[field.key] || '');
        if (field.key === 'accountNumber' && this.areaCode && value) {
          value = `+${this.areaCode} ${value}`;
        }
        return `${this.fieldLabel(field.key)}: ${value || '-'}`;
      }).join(', ');
    },
    async submitWithdraw() {
      if (this.previewing || this.submitting || this.submitDisabled || !this.validate()) return;
      const selectedConf = this.selectedConf;
      if (!selectedConf) return;
      const selectedBankCode = selectedConf.bank_code;
      const previewVersion = ++this.previewLoadVersion;
      const payload = {
        bank_code: selectedBankCode,
        amount: this.form.amount,
        payee_info: Object.assign({}, this.form.payee_info)
      };
      this.previewing = true;
      try {
        const resp = await this.$axios.post('/admin/exagent/console/funds/withdraw/preview/', payload);
        if (previewVersion !== this.previewLoadVersion) return;
        if (!this.applyVisible) return;
        if (String(this.form.bank_code) !== String(selectedBankCode)) return;
        if (resp.data.status !== 0) {
          this.clearConfirmation();
          return;
        }
        this.pendingPayload = payload;
        this.previewQuote = resp.data.data || null;
        this.confirmationPayMethod = selectedConf.name || selectedConf.bank_code;
        this.confirmationPayee = this.formatPayee(payload.payee_info, selectedConf.fields || []);
        this.confirmVisible = Boolean(this.previewQuote && this.previewQuote.fee_quote);
      } catch (error) {
        if (previewVersion === this.previewLoadVersion) this.clearConfirmation();
        return;
      } finally {
        if (previewVersion === this.previewLoadVersion) this.previewing = false;
      }
    },
    clearConfirmation() {
      this.confirmVisible = false;
      this.previewQuote = null;
      this.pendingPayload = null;
      this.confirmationPayMethod = '';
      this.confirmationPayee = '';
    },
    async confirmWithdraw() {
      if (this.submitting || !this.previewQuote || !this.pendingPayload) return;
      const payload = Object.assign({}, this.pendingPayload, {
        fee_quote: this.previewQuote.fee_quote
      });
      this.submitting = true;
      try {
        const resp = await this.$axios.post('/admin/exagent/console/funds/withdraw/apply/', payload);
        if (resp.data.status !== 0) {
          this.clearConfirmation();
          return;
        }
        this.clearConfirmation();
        this.$message.success(this.$t('exagentWithdraw.submitted'));
        this.applyVisible = false;
        this.search.page = 1;
        await Promise.all([this.loadWallet(), this.loadOptions(), this.loadData()]);
      } catch (error) {
        this.clearConfirmation();
        return;
      } finally {
        this.submitting = false;
      }
    },
    async loadData() {
      const resp = await this.$axios.get('/admin/exagent/console/funds/withdraw/orders/', { params: this.queryParams() });
      if (resp.data.status !== 0) return;
      const data = resp.data.data || {};
      this.tableData = data.list || [];
      this.count = data.count || 0;
    },
    async openDetail(row) {
      const resp = await this.$axios.get('/admin/exagent/console/funds/withdraw/orders/detail/', { params: { id: row.id } });
      if (resp.data.status !== 0) return;
      this.detail = resp.data.data || null;
      this.detailVisible = true;
    }
  }
};
</script>

<style lang="less" scoped>
.page-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.page-head .page-title {
  flex: 1;
}

.wallet-balance,
.apply-balance {
  display: flex;
  gap: 8px;
}

.wallet-balance {
  flex-direction: column;
  align-items: flex-end;
}

.wallet-balance span,
.apply-balance span {
  color: #778094;
  font-size: 12px;
}

.apply-balance {
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  background: #f4f9ff;
}

.apply-balance strong {
  color: #409eff;
  font-size: 18px;
}

.page-alert,
.dialog-alert {
  margin-bottom: 16px;
}

.form-control {
  width: 100%;
}

.form-tip {
  color: #778094;
  font-size: 12px;
  line-height: 1.5;
}

.field-grid,
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.field-grid {
  gap: 0 16px;
}

.detail-grid {
  gap: 10px;

  > div {
    min-width: 0;
    padding: 10px;
    border: 1px solid #e6e8ef;
    border-radius: 6px;
  }

  span {
    display: block;
    margin-bottom: 4px;
    color: #778094;
    font-size: 12px;
  }

  strong {
    overflow-wrap: anywhere;
  }
}

.section-title {
  margin: 18px 0 10px;
  font-size: 15px;
}

@media (max-width: 768px) {
  .field-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/EarningsWithdrawOrders.vue